import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Webhook,
  Zap,
  Key,
  ExternalLink,
  Plus,
  Settings,
  CheckCircle,
  Database
} from 'lucide-react';

export function IntegrationsSettings() {
  const integrations = [
    {
      name: 'Slack',
      description: 'Send notifications to Slack channels',
      icon: '💬',
      connected: true,
      status: 'active'
    },
    {
      name: 'Google Drive',
      description: 'Backup session data to Google Drive',
      icon: '📁',
      connected: false,
      status: 'available'
    },
    {
      name: 'Zapier',
      description: 'Connect with 3000+ apps via Zapier',
      icon: '⚡',
      connected: true,
      status: 'active'
    },
    {
      name: 'Microsoft Teams',
      description: 'Team collaboration and notifications',
      icon: '👥',
      connected: false,
      status: 'available'
    }
  ];

  const webhooks = [
    {
      id: 'wh_001',
      name: 'Training Session Alerts',
      url: 'https://api.example.com/webhooks/sessions',
      events: ['session.started', 'session.completed'],
      status: 'active'
    },
    {
      id: 'wh_002',
      name: 'Performance Updates',
      url: 'https://dashboard.myteam.com/cricket/webhook',
      events: ['shot.analyzed', 'performance.report'],
      status: 'active'
    }
  ];

  const apiKeys = [
    {
      id: 'key_001',
      name: 'Mobile App API Key',
      created: '2024-01-15',
      lastUsed: '2024-01-18',
      status: 'active'
    },
    {
      id: 'key_002',
      name: 'Analytics Dashboard',
      created: '2023-12-20',
      lastUsed: '2024-01-17',
      status: 'active'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Available Integrations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Zap className="mr-2 h-5 w-5" />
            Available Integrations
          </CardTitle>
          <CardDescription>
            Connect SixSight with your favorite tools and services
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {integrations.map((integration, index) => (
              <div key={index} className="p-4 border rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="text-2xl">{integration.icon}</div>
                    <div>
                      <h4 className="font-medium">{integration.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {integration.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end space-y-2">
                    {integration.connected ? (
                      <Badge variant="success">Connected</Badge>
                    ) : (
                      <Badge variant="outline">Available</Badge>
                    )}
                    <Button 
                      variant={integration.connected ? "outline" : "default"} 
                      size="sm"
                    >
                      {integration.connected ? 'Configure' : 'Connect'}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Webhooks */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <Webhook className="mr-2 h-5 w-5" />
                Webhooks
              </CardTitle>
              <CardDescription>
                Send real-time data to external services
              </CardDescription>
            </div>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Webhook
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {webhooks.map((webhook) => (
              <div key={webhook.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <h4 className="font-medium">{webhook.name}</h4>
                  <p className="text-sm text-muted-foreground font-mono">
                    {webhook.url}
                  </p>
                  <div className="flex items-center space-x-2 mt-1">
                    {webhook.events.map((event, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {event}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="success">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {webhook.status}
                  </Badge>
                  <Button variant="ghost" size="sm">
                    <Settings className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            {webhooks.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                No webhooks configured
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* API Keys */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <Key className="mr-2 h-5 w-5" />
                API Keys
              </CardTitle>
              <CardDescription>
                Manage API access for external applications
              </CardDescription>
            </div>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Generate API Key
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {apiKeys.map((apiKey) => (
              <div key={apiKey.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <h4 className="font-medium">{apiKey.name}</h4>
                  <p className="text-sm text-muted-foreground">
                    Created {new Date(apiKey.created).toLocaleDateString()} • 
                    Last used {new Date(apiKey.lastUsed).toLocaleDateString()}
                  </p>
                  <p className="text-xs text-muted-foreground font-mono mt-1">
                    {apiKey.id}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="success">{apiKey.status}</Badge>
                  <Button variant="ghost" size="sm">
                    Revoke
                  </Button>
                </div>
              </div>
            ))}
            {apiKeys.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                No API keys generated
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Data Export */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Database className="mr-2 h-5 w-5" />
            Data Export & Backup
          </CardTitle>
          <CardDescription>
            Export your organization's data for backup or analysis
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button variant="outline" className="h-auto p-4">
              <div className="text-center">
                <Database className="h-6 w-6 mx-auto mb-2" />
                <h4 className="font-medium">Export All Data</h4>
                <p className="text-xs text-muted-foreground">
                  Complete data backup in JSON format
                </p>
              </div>
            </Button>
            
            <Button variant="outline" className="h-auto p-4">
              <div className="text-center">
                <ExternalLink className="h-6 w-6 mx-auto mb-2" />
                <h4 className="font-medium">Analytics Export</h4>
                <p className="text-xs text-muted-foreground">
                  Performance data for external analysis
                </p>
              </div>
            </Button>
          </div>
          
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900">Automatic Backups</h4>
            <p className="text-sm text-blue-700 mt-1">
              Daily backups are automatically created and stored securely. 
              You can restore from backups up to 30 days old.
            </p>
            <Button variant="outline" size="sm" className="mt-2">
              View Backup History
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Integration Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Integration Settings</CardTitle>
          <CardDescription>
            Configure global integration preferences
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <h4 className="font-medium">Rate Limiting</h4>
              <p className="text-sm text-muted-foreground">
                Prevent excessive API calls to protect system performance
              </p>
            </div>
            <Switch defaultChecked={true} />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <h4 className="font-medium">Error Notifications</h4>
              <p className="text-sm text-muted-foreground">
                Get notified when integrations fail or encounter errors
              </p>
            </div>
            <Switch defaultChecked={true} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}